import { useEffect } from 'react';
import type { Key } from 'react';
import type { Control, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import {
  metricDataSourceValueColumnOptional,
  warehouseHostedMetricsSupportPercentiles,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import {
  AnalysisTypeDisplayOptions,
  SuccessCriteriaDisplayOptions,
  VALID_ANALYSIS_PERCENTILES,
} from '@gonfalon/metrics/constants';
import { getAnalysisTypeOption } from '@gonfalon/metrics/getAnalysisTypeOption';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import type { Metric } from '@gonfalon/metrics/types';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, FieldGroup, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { MetricRandomizationUnits, RandomizationUnitsError } from '../components/MetricRandomizationUnits';
import { RequiredLabel } from '../components/RequiredLabel';
import type { AggregationOptionKey, MetricForm, RandomizationUnitOption } from '../formUtils';
import { aggregationOptions } from '../formUtils';

import styles from './MetricDefinitionFormField.module.css';

interface MetricDefinitionFormFieldProps {
  context: UseFormReturn<MetricForm>;
}

const AGGREGATION_KEYS_BY_KIND: Partial<Record<NonNullable<Metric['kind']>, AggregationOptionKey[]>> = {
  pageview: ['count', 'count-distinct-units'],
  click: ['count', 'count-distinct-units'],
  // Traces support the full set (like custom) — we list the keys here only to
  // swap in span-specific descriptions and drop the metric-kind tags.
  trace: ['count', 'count-distinct-units', 'sum', 'average'],
};

const AGGREGATION_DESCRIPTIONS_BY_KIND: Partial<
  Record<NonNullable<Metric['kind']>, Partial<Record<AggregationOptionKey, string>>>
> = {
  pageview: {
    count: 'Number of times a page was viewed',
    'count-distinct-units': 'Whether a page was viewed',
  },
  click: {
    count: 'Number of times a target was clicked',
    'count-distinct-units': 'Whether a target was clicked',
  },
  trace: {
    count: 'Number of matching spans',
    'count-distinct-units': 'Whether a matching span is seen at least once',
    sum: 'Total span attribute value per unit',
    average: 'Average span attribute value per unit',
  },
};

function getAggregationOptionsForKind(metricKind: Metric['kind']) {
  const allowedKeys = metricKind ? AGGREGATION_KEYS_BY_KIND[metricKind] : undefined;
  if (!allowedKeys) {
    return aggregationOptions;
  }
  const descriptions = metricKind ? AGGREGATION_DESCRIPTIONS_BY_KIND[metricKind] : undefined;
  return aggregationOptions
    .filter((o) => allowedKeys.includes(o.key))
    .map((o) => ({ ...o, tag: '', description: descriptions?.[o.key] ?? o.description }));
}

function getSentenceNouns(metricKind: Metric['kind']): {
  countNoun: string;
  valueNoun: string;
  occurrenceClause: string;
} {
  switch (metricKind) {
    case 'pageview':
      return { countNoun: 'page views', valueNoun: 'event values', occurrenceClause: 'units that saw the page' };
    case 'click':
      return { countNoun: 'clicks', valueNoun: 'event values', occurrenceClause: 'units that clicked' };
    case 'trace':
      return {
        countNoun: 'matching spans',
        valueNoun: 'span attribute values',
        occurrenceClause: 'units with a matching span',
      };
    default:
      return { countNoun: 'events', valueNoun: 'event values', occurrenceClause: 'units that sent the event' };
  }
}

function getAggregationKey(
  measureType: MetricForm['measureType'],
  unitAggregationType: MetricForm['unitAggregationType'],
): AggregationOptionKey | null {
  if (measureType === 'count') {
    return 'count';
  }
  if (measureType === 'occurrence') {
    return 'count-distinct-units';
  }
  if (measureType === 'value') {
    if (unitAggregationType === 'sum') {
      return 'sum';
    }
    if (unitAggregationType === 'average') {
      return 'average';
    }
  }
  return null;
}

export const MetricDefinitionFormField: React.FC<MetricDefinitionFormFieldProps> = ({ context }) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = context;

  const selectedMeasureType = useWatch({ control, name: 'measureType', exact: true });
  const percentile = useWatch({ control, name: 'percentileValue', exact: true });
  const analysisType = useWatch({ control, name: 'analysisType', exact: true });
  const successCriteria = useWatch({
    control,
    name: 'successCriteria',
    exact: true,
  });
  const unitAggregationType = useWatch({ control, name: 'unitAggregationType', exact: true });
  const metricKind = useWatch({ control, name: 'kind', exact: true });
  const randomizationUnits = useWatch({ control, name: 'randomizationUnits', exact: true });
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const projectKey = useProjectKey();
  const warehousePercentilesEnabled = warehouseHostedMetricsSupportPercentiles();
  const { data: dataSourcesData, isPending: isDataSourcesPending } = useQuery({
    ...getMetricDataSourcesQuery({ projKey: projectKey }),
    enabled: warehousePercentilesEnabled && Boolean(dataSourceKeyFormValue) && !isLDHosted(dataSourceKeyFormValue),
  });
  const selectedDataSource = dataSourcesData?.items?.find((dataSource) => dataSource.key === dataSourceKeyFormValue);
  const supportsPercentiles =
    isLDHosted(dataSourceKeyFormValue) ||
    (warehousePercentilesEnabled && selectedDataSource?.integrationKey === 'snowflake-experimentation');
  const percentileSupportResolved =
    isLDHosted(dataSourceKeyFormValue) || !warehousePercentilesEnabled || !isDataSourcesPending;

  useEffect(() => {
    if (percentileSupportResolved && !supportsPercentiles && analysisType === 'percentile') {
      setValue('analysisType', 'mean');
      setValue('percentileValue', undefined);
      setValue('eventDefault', { disabled: false, value: 0 });
    }
  }, [analysisType, percentileSupportResolved, setValue, supportsPercentiles]);

  const useAggregationDropdown =
    metricKind === 'custom' || metricKind === 'pageview' || metricKind === 'click' || metricKind === 'trace';

  if (!selectedMeasureType && !useAggregationDropdown) {
    return null;
  }

  const metricDefinitionError = [
    errors.measureType?.message,
    errors.unitAggregationType?.message,
    errors.analysisType?.message,
    errors.successCriteria?.message,
  ]
    .filter(Boolean)
    .join(', ');

  const randomizationUnitsOnSelect = (selection: string[], options: RandomizationUnitOption[]) => {
    const selectedOptions = options.filter((option) => selection.includes(option.value));
    setValue('randomizationUnits', selectedOptions, { shouldDirty: true });
  };

  const aggregationKey = getAggregationKey(selectedMeasureType, unitAggregationType);

  const sentencePrefix = aggregationKey
    ? aggregationOptions.find((o) => o.key === aggregationKey)?.sentencePrefix
    : null;

  const aggregationOptionsForKind = getAggregationOptionsForKind(metricKind);
  const sentenceNouns = getSentenceNouns(metricKind);

  const randomizationUnitsForm = (
    <MetricRandomizationUnits
      initialValues={randomizationUnits.map((ru) => ru.value)}
      onSelect={randomizationUnitsOnSelect}
      dataSourceKey={dataSourceKeyFormValue}
    />
  );

  return (
    <FieldGroup errorMessage={metricDefinitionError}>
      <RequiredLabel label="Metric definition" htmlFor="metricDefinition" />
      <span className={styles.metricDefinition} id="metricDefinition">
        <AggregationMethodPicker
          control={control}
          aggregationKey={aggregationKey}
          sentencePrefix={sentencePrefix ?? null}
          options={aggregationOptionsForKind}
          setValue={setValue}
          clearErrors={context.clearErrors}
        />
        {aggregationKey && sentencePrefix && aggregationKey === 'count-distinct-units' && (
          <>
            of {randomizationUnitsForm} {sentenceNouns.occurrenceClause} where{' '}
            {metricKind === 'custom' || metricKind === 'trace' ? (
              <SuccessCriteriaPicker currentSuccessCriteria={successCriteria} setValue={setValue} />
            ) : (
              'higher is better'
            )}
          </>
        )}
        {aggregationKey && sentencePrefix && aggregationKey !== 'count-distinct-units' && (
          <>
            {aggregationKey === 'count' ? `${sentenceNouns.countNoun} per` : `${sentenceNouns.valueNoun} per`}{' '}
            {randomizationUnitsForm}, then compute the{' '}
            {supportsPercentiles ? (
              <AnalysisTypePicker
                currentAnalysisType={analysisType}
                currentPercentileValue={percentile}
                setValue={setValue}
              />
            ) : (
              <span>Average</span>
            )}{' '}
            of those values where{' '}
            {metricKind === 'custom' || metricKind === 'trace' ? (
              <SuccessCriteriaPicker currentSuccessCriteria={successCriteria} setValue={setValue} />
            ) : (
              'higher is better'
            )}
          </>
        )}
      </span>
      <RandomizationUnitsError message={errors.randomizationUnits?.message} dataSourceKey={dataSourceKeyFormValue} />
    </FieldGroup>
  );
};

interface AggregationMethodPickerProps {
  control: Control<MetricForm>;
  aggregationKey: AggregationOptionKey | null;
  sentencePrefix: string | null;
  options: typeof aggregationOptions;
  setValue: UseFormSetValue<MetricForm>;
  clearErrors: UseFormReturn<MetricForm>['clearErrors'];
}

// MTRX-2050: when the selected data source has no `valueColumn` mapping,
// numeric metric kinds (Sum / Average) can't produce a meaningful query, so
// hide those options from the picker. The DS-level flag governs whether the
// underlying scenario is even possible.
const useHideNumericOptions = (control: Control<MetricForm>): boolean => {
  const projectKey = useProjectKey();
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const flagOn = metricDataSourceValueColumnOptional();
  const { data } = useQuery({
    ...getMetricDataSourcesQuery({ projKey: projectKey }),
    enabled: flagOn && Boolean(dataSourceKey) && !isLDHosted(dataSourceKey),
  });
  if (!flagOn || !dataSourceKey || isLDHosted(dataSourceKey)) {
    return false;
  }
  const selectedDataSource = data?.items?.find((ds) => ds.key === dataSourceKey);
  if (!selectedDataSource) {
    return false;
  }
  const valueColumn = selectedDataSource.columnMappings?.valueColumn;
  return !valueColumn || valueColumn.trim().length === 0;
};

const AggregationMethodPicker: React.FC<AggregationMethodPickerProps> = ({
  control,
  aggregationKey,
  sentencePrefix,
  options,
  setValue,
  clearErrors,
}) => {
  const hideNumericOptions = useHideNumericOptions(control);
  const visibleOptions = hideNumericOptions
    ? options.filter((option) => option.key !== 'sum' && option.key !== 'average')
    : options;

  function handleSelect(key: Key | null) {
    // Defense in depth: Sum/Average are hidden from the listbox when the data
    // source has no value column, but guard against a programmatic value change
    // (e.g. reopening a numeric metric in the edit flow) reaching this path.
    if (hideNumericOptions && (key === 'sum' || key === 'average')) {
      return;
    }
    if (key === 'count') {
      setValue('measureType', 'count', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('unit', undefined);
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'sum', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', false, { shouldDirty: true });
    } else if (key === 'count-distinct-units') {
      setValue('measureType', 'occurrence', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('unit', undefined);
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'average', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', false, { shouldDirty: true });
    } else if (key === 'sum') {
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'sum', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', true, { shouldDirty: true });
    } else if (key === 'average') {
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'average', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', true, { shouldDirty: true });
    } else {
      return;
    }
    clearErrors(['measureType', 'unitAggregationType']);
  }

  return (
    <>
      <Controller
        control={control}
        name="measureType"
        rules={{ required: 'Selection is required' }}
        render={() => (
          <Select
            onChange={handleSelect}
            value={aggregationKey}
            aria-label="Aggregation method"
            placeholder="Select aggregation"
            className={styles.aggregationMethodSelect}
          >
            <Button data-test-id="aggregation-method-select">
              <SelectValue>{({ isPlaceholder }) => (isPlaceholder ? 'Select' : (sentencePrefix ?? ''))}</SelectValue>
              <Icon name="chevron-down" size="small" />
            </Button>
            <Popover className={styles.aggregationMethodPopover}>
              <ListBox items={visibleOptions}>
                {(item) => (
                  <ListBoxItem id={item.key} textValue={item.name} aria-label={item.name}>
                    <div className={styles.aggregationOptionLabel}>
                      <div className={styles.aggregationOptionNameRow}>
                        <span className={styles.aggregationOptionName}>{item.name}</span>
                        {item.tag ? <span className={styles.aggregationOptionTag}>{item.tag}</span> : null}
                      </div>
                      <span className={styles.aggregationOptionDescription}>{item.description}</span>
                    </div>
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </Select>
        )}
      />
      <Controller
        control={control}
        name="unitAggregationType"
        rules={{
          validate: (value, formValues) =>
            formValues.measureType === 'value' ? !!value || 'Event value aggregation is required' : true,
        }}
        render={() => <></>}
      />
    </>
  );
};

interface AnalysisTypePickerProps {
  currentAnalysisType?: Metric['analysisType'];
  currentPercentileValue?: number;
  setValue: UseFormSetValue<MetricForm>;
}
const AnalysisTypePicker: React.FC<AnalysisTypePickerProps> = (props: AnalysisTypePickerProps) => {
  const selectedKey = getAnalysisTypeOption(props.currentAnalysisType, props.currentPercentileValue);
  function handleAnalysisTypeChange(key: Key | null) {
    if (Number.isInteger(key) && VALID_ANALYSIS_PERCENTILES.includes(Number(key))) {
      props.setValue('analysisType', 'percentile', { shouldDirty: true });
      // We need to update eventDefault to be disabled here - it is not supported for percentile analysis
      props.setValue('eventDefault', { disabled: true }, { shouldDirty: true });
      props.setValue('percentileValue', Number(key), { shouldDirty: true });
    } else {
      props.setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      props.setValue('analysisType', 'mean', { shouldDirty: true });
      props.setValue('percentileValue', undefined, { shouldDirty: true });
    }
  }
  return (
    <Select
      onChange={handleAnalysisTypeChange}
      value={selectedKey}
      aria-label="Choose a method for unit to sample aggregation"
      className={styles.analysisTypeSelect}
    >
      <Button>
        <SelectValue />
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover className={styles.popover}>
        <ListBox>
          {AnalysisTypeDisplayOptions.map((option) => (
            <ListBoxItem key={option.value} id={option.value}>
              {option.displayName}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
};

interface SuccessCriteriaPickerProps {
  currentSuccessCriteria?: string;
  setValue: UseFormSetValue<MetricForm>;
}
const SuccessCriteriaPicker: React.FC<SuccessCriteriaPickerProps> = (props: SuccessCriteriaPickerProps) => {
  function handleSuccessCriteriaChange(key: Key | null) {
    if (key === 'HigherThanBaseline' || key === 'LowerThanBaseline') {
      props.setValue('successCriteria', key, { shouldDirty: true });
    }
  }
  return (
    <Select
      onChange={handleSuccessCriteriaChange}
      value={props.currentSuccessCriteria ?? 'HigherThanBaseline'}
      aria-label="Choose a success criteria"
    >
      <Button data-test-id="success-criteria-button">
        <SelectValue />
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover className={styles.popover}>
        <ListBox>
          {SuccessCriteriaDisplayOptions.map((option) => (
            <ListBoxItem key={option.value} id={option.value}>
              {option.displayName}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
};
